<template>
    <div class="patient-list">
        <data-table ref="dataTable"
                    table-id="patient-table"
                    :show-filters="true"
                    :show-pager="true"
                    :show-details="false"
                    :get-items="getPatients"
                    :filters="filters"
                    :fields="fields">

            <!-- custom template for filters -->
            <template v-slot:filters>
                <b-row cols="5">
                    <b-col>
                        <b-form-group horizontal
                                      :label="$i18n.gt('LABEL_FILTER')">
                            <b-form-select id="filter-select" placeholder="Select one"
                                           v-model="filters.filter.value"
                                           :options="filterOptions"
                                           @input="patientFiltersChanged()">
                            </b-form-select>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group horizontal
                                      :label="$i18n.gt('LABEL_FIRST_LAST_NAME')">
                            <b-form-input v-model="filters.text.value"
                                          @input="patientFiltersChanged()"/>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group horizontal
                                      :label="$i18n.gt('LABEL_DOB')">
                            <b-form-input type="date"
                                          v-model="filters.dob.value"
                                          @input="patientFiltersChanged()"/>
                        </b-form-group>
                    </b-col>
                    <b-col>
                        <b-form-group horizontal
                                      :label="$i18n.gt('LABEL_EMAIL')">
                            <b-form-input v-model="filters.email.value"
                                          @input="patientFiltersChanged()"/>
                        </b-form-group>
                    </b-col>
                </b-row>
            </template>

            <!-- template for custom columns -->
            <template v-slot:customField="slotProps">
                <div v-if="slotProps.row.field.key == 'participant'">
                    <patient-id :participant="slotProps.row.item.participant"></patient-id>
                    <b-badge variant="warning" class="mt-1"
                             v-if="slotProps.row.item.participant.lacksPassword">
                        {{ $i18n.gt('BADGE_NO_PASSWORD') }}
                    </b-badge>
                    <b-badge variant="danger" class="ml-3"
                             v-if="slotProps.row.item.disabled">
                        {{ $i18n.gt('LABEL_DISABLED') }}
                    </b-badge>
                </div>
                <div v-else-if="slotProps.row.field.key == 'preferredContact'">
                    <patient-contact-methods-display :participant="slotProps.row.item.participant"></patient-contact-methods-display>
                </div>
                <div v-else-if="slotProps.row.field.key == 'lastActiveTime'">
                    <timestamp :date="slotProps.row.item.participant.lastActiveTime"></timestamp>
                </div>
                <div v-else-if="slotProps.row.field.key == 'lastnote'">
                    <b-row no-gutters class="mb-2"
                           v-for="(note, index) in getNotesToShow(slotProps.row.item.participant.surveyNotes)"
                           :key="index">
                        <b-col class="text-center">
                            <timestamp :date="note.drafted"></timestamp>
                        </b-col>
                        <b-col class="text-left">
                            <b-badge variant="warning"
                                     v-if="isDraft(note)">
                                {{ $i18n.gt('BADGE_DRAFT') }}
                            </b-badge>
                            <b-badge variant="danger"
                                     v-if="needsSuperbill(note)">
                                {{ $i18n.gt('BADGE_NO_BILL') }}
                            </b-badge>
                        </b-col>
                    </b-row>
                </div>
                <div v-else-if="slotProps.row.field.key == 'appointment'">
                    <patient-appointment :provider="slotProps.row.item">
                    </patient-appointment>
                </div>
                <div v-else-if="slotProps.row.field.key == 'actions'">
                    <b-btn class="btn btn-ghost-blue btn-sm btn-icon mr-2"
                           :to="`/participant/details/id/${slotProps.row.item.participant.id}`">
                        <shs-icon name="clipboard"></shs-icon>
                    </b-btn>
                    <b-btn class="btn btn-ghost-blue btn-sm btn-icon"
                           :to="`/participant/chart/id/${slotProps.row.item.participant.id}`">
                        <shs-icon name="medical-cross"></shs-icon>
                    </b-btn>
                </div>
            </template>

        </data-table>

    </div>
</template>

<!-- ----------------------------------------------------------------------- -->

<script>
import Vue from 'vue'
import VueResource from 'vue-resource'
import * as _ from 'lodash'
import SurveyNote from "@/providers/SurveyNote"

Vue.use(VueResource)

/**
 * Table listing a Practitioner's (Practice's) Participants, with specialized filters
 */
export default Vue.extend({
    name: 'patient-list',
    props: {
        filterValue: {type: String, default: null}
    },
    data() {
        return {}
    },
    computed: {
        practiceId() {
            return this.$user.practitionerPracticeId()
        },
        filterOptions() {
            return [
                {value: null, text: this.$i18n.gt('LABEL_ALL_PATIENTS')},
                {value: 'appointment_alert', text: this.$i18n.gt('LABEL_APPOINTMENT_ALERT')},
                {value: 'eval_due', text: this.$i18n.gt('LABEL_EVAL_DUE')},
                {value: 'all_ccm', text: this.$i18n.gt('LABEL_ALL_CCM')},
                {value: 'ccm_due', text: this.$i18n.gt('LABEL_CCM_DUE')},
                {value: 'draft_note', text: this.$i18n.gt('LABEL_DRAFT_NOTE')},
                {value: 'missing_superbill', text: this.$i18n.gt('LABEL_MISSING_SUPERBILL')},
            ]
        },
        months() {
            return [
                {value: null, text: ''},
                {value: 1, text: 'January'},
                {value: 2, text: 'February'},
                {value: 3, text: 'March'},
                {value: 4, text: 'April'},
                {value: 5, text: 'May'},
                {value: 6, text: 'June'},
                {value: 7, text: 'July'},
                {value: 8, text: 'August'},
                {value: 9, text: 'September'},
                {value: 10, text: 'October'},
                {value: 11, text: 'November'},
                {value: 12, text: 'December'},
            ]
        },
        filters() {
            return {
                filter: {field: 'filter', value: this.filterValue},
                text: {field: 'text', value: null},
                email: {field: 'email', value: null},
                dob: {field: 'dob', value: null},
                birth_month: {field: 'birth_month', value: null},
            }
        },
        fields() {
            return [
                {key: 'participant', sortable: true, label: this.$i18n.gt('LABEL_PARTICIPANT')},
                {key: 'preferredContact', sortable: false, label: this.$i18n.gt('LABEL_PREFERRED_CONTACT')},
                {key: 'lastnote', sortable: false, label: this.$i18n.gt('LABEL_LAST_NOTE')},
                {key: 'appointment', sortable: true, label: this.$i18n.gt('LABEL_APPOINTMENT')},
                {key: 'actions', sortable: false, custom: true, label: ''},
            ]
        },
    },
    methods: {
        getPatients(listParams) {
            return this.$api.getPatients(this, this.filters, listParams, this.$user.practitionerPracticeId())
        },
        patientFiltersChanged() {
            this.$refs.dataTable.debouncedRefresh()
        },
        getNotesToShow(notes) {
            if (!notes.length) {
                return []
            } else {
                const badgedNotes = _.filter(notes, (note) => {
                    return this.needsSuperbill(note) || this.isDraft(note)
                })
                return _.union([notes[0]], badgedNotes)
            }
        },
        needsSuperbill(note) {
            return SurveyNote.hasNoBill(note)
        },
        isDraft(note) {
            return SurveyNote.isDraft(note)
        },
        preferredContactIncludes(participant, method) {
            console.log(participant.preferred_contact_method)
            return !!participant.preferred_contact_method && participant.preferred_contact_method.indexOf(method) != -1
        }
    },
    watch: {
        // reload patient list when practitioner switches practices
        practiceId: {
            handler: function (newVal, oldVal) {
                this.$refs.dataTable.debouncedRefresh()
            }
        }
    }
})

</script>

<!-- ----------------------------------------------------------------------- -->

<style lang="scss">
#filter-radios {
    .custom-radio {
        display: block;
    }
}
</style>
